import { useCallback, useEffect, useRef } from 'react';
import type { DiscussionPeerActivityEvent } from '@jarvis/platform-file-review';
import { showDiscussionMessageToast } from './discussionMessageToast';
import {
  DISCUSSION_NOTIFICATION_PREFS_CHANGED_EVENT,
  hasAnyDiscussionNotificationChannelEnabled,
  readDiscussionNotificationPrefs
} from './discussionNotificationPrefs';
import { playDiscussionAlertSound } from './discussionNotificationSound';
import { showDiscussionSystemNotification } from './discussionSystemNotification';

export interface DiscussionNotificationDeliveryEvent extends DiscussionPeerActivityEvent {
  channels: Array<'sound' | 'toast' | 'system'>;
}

export interface UseDiscussionMessageNotificationsOptions {
  enabled: boolean;
  onOpenDiscussionPanel: () => void;
  onTrackEvent?: (event: DiscussionNotificationDeliveryEvent) => void;
}

export function useDiscussionMessageNotifications({
  enabled,
  onOpenDiscussionPanel,
  onTrackEvent
}: UseDiscussionMessageNotificationsOptions) {
  const prefsRef = useRef(readDiscussionNotificationPrefs());
  const onOpenDiscussionPanelRef = useRef(onOpenDiscussionPanel);
  onOpenDiscussionPanelRef.current = onOpenDiscussionPanel;
  const onTrackEventRef = useRef(onTrackEvent);
  onTrackEventRef.current = onTrackEvent;

  useEffect(() => {
    const syncPrefs = () => {
      prefsRef.current = readDiscussionNotificationPrefs();
    };
    window.addEventListener(DISCUSSION_NOTIFICATION_PREFS_CHANGED_EVENT, syncPrefs);
    return () => window.removeEventListener(DISCUSSION_NOTIFICATION_PREFS_CHANGED_EVENT, syncPrefs);
  }, []);

  const onPeerActivity = useCallback(
    (event: DiscussionPeerActivityEvent) => {
      if (!enabled) return;

      onOpenDiscussionPanelRef.current();

      const prefs = prefsRef.current;
      if (!hasAnyDiscussionNotificationChannelEnabled(prefs)) return;

      void (async () => {
        const channels: DiscussionNotificationDeliveryEvent['channels'] = [];

        if (prefs.soundEnabled) {
          playDiscussionAlertSound();
          channels.push('sound');
        }
        if (prefs.toastEnabled) {
          showDiscussionMessageToast(event);
          channels.push('toast');
        }
        if (prefs.systemEnabled && (await showDiscussionSystemNotification(event))) {
          channels.push('system');
        }

        if (channels.length > 0) {
          onTrackEventRef.current?.({ ...event, channels });
        }
      })();
    },
    [enabled]
  );

  return { onPeerActivity };
}
